import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
} from "recharts";
import MetricCard from "@/components/MetricCard";
import TreasuryCurveCard from "@/components/TreasuryCurveCard";
import ChartModal from "@/components/ChartModal";

const HEADLINE = "Treasury Curve Positioning";
const DESCRIPTION =
    "Positioning data from the CFTC Commitments of Traders (CoT) report, focused on U.S. Treasury futures across the yield curve and macro-linked contracts.";
const RHYTHM = "— CoT weekly update every Friday —";

let _fontInjected = false;
function ensureDmSans() {
    if (_fontInjected || typeof document === "undefined") return;
    _fontInjected = true;
    const links = [
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
        { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap" },
    ];
    links.forEach((cfg) => {
        const el = document.createElement("link");
        Object.entries(cfg).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
    });
}

export default function TreasuryCurvePage() {
    const BACKEND = process.env.REACT_APP_BACKEND_URL ?? "";
    const API = `${BACKEND}/api/cot`;

    const [metrics, setMetrics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [macroModal, setMacroModal] = useState(null);

    useEffect(() => {
        ensureDmSans();
        let alive = true;
        axios.get(`${API}/metrics`)
            .then((r) => {
                if (!alive) return;
                setMetrics(r.data.metrics);
                setLoading(false);
            })
            .catch(() => {
                if (alive) { toast.error("Could not load metrics."); setLoading(false); }
            });
        return () => { alive = false; };
    }, [API]);

    const macroMetrics = metrics.filter((m) => ["0105", "0106"].includes(m.id));

    return (
        <div className="App" data-testid="treasury-curve-page">
            <Toaster position="bottom-right" theme="dark" toastOptions={{ style: { background: "rgba(34,37,24,0.92)", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", backdropFilter: "blur(12px)" } }} />

            <header className="px-6 sm:px-10 lg:px-16 pt-14 pb-12">
                <div className="max-w-[1200px] mx-auto">
                    <div className="pord-eyebrow">Pord.</div>
                    <h1 className="pord-heading mt-8 text-3xl sm:text-4xl lg:text-[44px] max-w-4xl text-white">
                        {HEADLINE}
                    </h1>
                    <p className="pord-body mt-6 max-w-3xl text-[15px] sm:text-base">{DESCRIPTION}</p>
                    <p className="mt-5 text-sm font-light tracking-wide" style={{ color: "var(--pord-teal)" }}>{RHYTHM}</p>
                </div>
            </header>

            <div className="pord-divider max-w-[1200px] mx-auto" />

            <main className="px-6 sm:px-10 lg:px-16 pt-12 pb-24">
                <div className="max-w-[1200px] mx-auto">
                    {loading && (
                        <div className="pord-body py-24 text-center">
                            <FileSpreadsheet className="inline-block mb-4 opacity-40" size={28} />
                            <div>parsing data...</div>
                        </div>
                    )}

                    {!loading && (
                        <>
                            {/* Section 1: Treasury Curve */}
                            <SectionLabel label="Section 1 · Treasury Curve Positioning" />
                            <div className="mb-20">
                                <TreasuryCurveCard api={API} />
                            </div>

                            {/* Section 2: Macro Reads */}
                            <SectionLabel label="Section 2 · Macro Reads" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {macroMetrics.map((m) => (
                                    <MacroPreview
                                        key={m.id}
                                        metric={m}
                                        api={API}
                                        onClick={() => setMacroModal(m.id)}
                                    />
                                ))}
                            </div>

                            {/* Macro Modal */}
                            <ChartModal
                                open={macroModal !== null}
                                onClose={() => setMacroModal(null)}
                                title={macroModal ? `CARD ${macroModal}` : ""}
                            >
                                {macroModal && (
                                    <MetricCard
                                        metricId={macroModal}
                                        summary={macroMetrics.find((m) => m.id === macroModal)}
                                        api={API}
                                    />
                                )}
                            </ChartModal>
                        </>
                    )}
                </div>
            </main>

            <footer className="px-6 sm:px-10 lg:px-16 py-10 max-w-[1200px] mx-auto">
                <div className="pord-divider" />
            </footer>
        </div>
    );
}

function SectionLabel({ label }) {
    return (
        <div className="mb-8 mt-4">
            <div className="pord-eyebrow" style={{ fontSize: 12, letterSpacing: "0.2em" }}>{label}</div>
            <div className="pord-divider mt-3" />
        </div>
    );
}

const MACRO_DESC = {
    "0105": "This panel shows how leveraged funds (fast money) are positioning the U.S. Dollar Index futures. It helps identify whether current positioning looks stretched, which can be useful when looking to gauge potential upside or downside event across financial markets.",
    "0106": "This panel is charting on 3M SOFR futures. Reflect a priced view of the Asset Managers (Long Money) on the near-term path for front-end funding rates.",
};

function MacroPreview({ metric, api, onClick }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        let alive = true;
        axios.get(`${api}/metrics/${metric.id}`)
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => {});
        return () => { alive = false; };
    }, [api, metric.id]);

    const chartData = useMemo(() => {
        if (!data?.series) return [];
        return data.series
            .filter((d) => d.net_pct_oi != null)
            .slice(-104) // last 2 years
            .map((d) => ({ t: new Date(d.date).getTime(), v: d.net_pct_oi }));
    }, [data]);

    const current = data?.current;
    const rank = current?.pct_rank_52w;
    const desc = MACRO_DESC[metric.id] || "";

    return (
        <button
            data-testid={`card-preview-${metric.id}`}
            onClick={onClick}
            style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                cursor: "pointer",
                background: "linear-gradient(180deg, rgba(48,60,32,0.4) 0%, rgba(22,28,14,0.7) 100%)",
                border: "1px solid rgba(184,212,208,0.12)",
                borderRadius: 18,
                padding: "22px 20px",
                transition: "border-color 200ms ease, box-shadow 200ms ease",
                overflow: "hidden",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(184,212,208,0.3)";
                e.currentTarget.style.boxShadow = "0 8px 32px -8px rgba(0,0,0,0.4)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(184,212,208,0.12)";
                e.currentTarget.style.boxShadow = "none";
            }}
        >
            <div className="pord-label mb-2" style={{ fontSize: 9 }}>Card {metric.id} · click to expand</div>

            <div className="flex items-start justify-between gap-4">
                <h3 className="pord-heading text-lg sm:text-xl text-white">{metric.display_name}</h3>
                {rank != null && (
                    <span className="text-2xl font-light tabular-nums uppercase shrink-0" style={{ color: "var(--pord-teal)", letterSpacing: "0.04em" }}>
                        {Math.round(rank)}th <span className="text-[10px]" style={{ color: "var(--pord-text-muted)" }}>pctile</span>
                    </span>
                )}
            </div>

            {/* Mini chart */}
            {chartData.length > 0 && (
                <div style={{ width: "100%", height: 80, marginTop: 12, pointerEvents: "none" }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                            <defs>
                                <linearGradient id={`mf_${metric.id}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0" stopColor="#B8D4D0" stopOpacity={0.25} />
                                    <stop offset="1" stopColor="#B8D4D0" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="t" hide />
                            <YAxis hide />
                            <Area type="monotone" dataKey="v" stroke="rgba(184,212,208,0.5)" strokeWidth={1} fill={`url(#mf_${metric.id})`} dot={false} isAnimationActive={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}

            <p className="pord-body mt-3" style={{ fontSize: 13, lineHeight: 1.7, color: "rgba(200,205,195,0.78)" }}>
                {desc}
            </p>
        </button>
    );
}
